import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageDescription } from '@/components/page-description';
import { PageTitle } from '@/components/page-title';
import { ApiKeysSkeleton } from '@/pages/api-keys/api-keys-skeleton';
import { useNotification } from '@/components/notifications';
import { useGetUserPats } from '@/hooks/queries/user/use-get-user-pats';
import { useCreateUserPat } from '@/hooks/mutations/user/use-create-user-pat';
import { useRevokeUserPat } from '@/hooks/mutations/user/use-revoke-user-pat';
import { formatDate } from '@/utils/format-date';
import type { UserPat } from '@/services/user/create-user-pat';
import { RevealNewTokenModal, RevokeTokenModal } from './token-modals';

const DEFAULT_EXPIRY_DAYS = 90;

export const ApiTokensPage = () => {
  const notification = useNotification();
  const { data: tokens = [], isPending, isError } = useGetUserPats();

  const [name, setName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(DEFAULT_EXPIRY_DAYS);

  // Plaintext token from the create response — only ever available once.
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [tokenToRevoke, setTokenToRevoke] = useState<UserPat | null>(null);

  const { mutate: createToken, isPending: isCreating } = useCreateUserPat({
    onSuccess: (created) => {
      setRevealedToken(created.token);
      setName('');
      setExpiresInDays(DEFAULT_EXPIRY_DAYS);
    },
    onError: (error) => {
      notification.error(error.message || 'Failed to create API token');
    },
  });

  const { mutate: revokeToken, isPending: isRevoking } = useRevokeUserPat({
    onSuccess: () => {
      notification.success('API token revoked');
      setTokenToRevoke(null);
    },
    onError: (error) => {
      notification.error(error.message || 'Failed to revoke API token');
    },
  });

  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    const clampedExpiresInDays = Math.min(3650, Math.max(1, expiresInDays));
    createToken({ name: trimmed, expiresInDays: clampedExpiresInDays });
  };

  if (isPending) {
    return <ApiKeysSkeleton />;
  }

  return (
    <div className='flex flex-col gap-6 w-full mb-10'>
      <div>
        <PageTitle id='api-tokens-title' title='Personal API tokens' />
        <PageDescription>
          Personal API tokens authenticate API requests as you — use them for
          the file Upload API. These are different from your organization&apos;s
          Capitol API keys under Settings → API keys.
        </PageDescription>
      </div>

      <Card>
        <CardContent>
          <form
            className='flex flex-col gap-4 sm:flex-row sm:items-end'
            onSubmit={handleGenerate}
          >
            <div className='flex-1 space-y-1.5'>
              <Label htmlFor='token-name'>Token name</Label>
              <Input
                id='token-name'
                placeholder='e.g. Upload script'
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className='w-full sm:w-40 space-y-1.5'>
              <Label htmlFor='token-expiry'>Expires (days)</Label>
              <Input
                id='token-expiry'
                type='number'
                min={1}
                max={3650}
                value={expiresInDays}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  setExpiresInDays(Number.isNaN(n) ? DEFAULT_EXPIRY_DAYS : n);
                }}
              />
            </div>
            <Button
              id='api-tokens-generate'
              type='submit'
              disabled={isCreating || !name.trim()}
            >
              {isCreating && <Loader2 className='size-4 animate-spin' />}
              Generate token
            </Button>
          </form>
        </CardContent>
      </Card>

      {isError ? (
        <p id='api-tokens-error' className='text-destructive'>
          Couldn&apos;t load your API tokens. Please refresh to try again.
        </p>
      ) : tokens.length === 0 ? (
        <p id='api-tokens-empty' className='text-zinc-500 dark:text-zinc-50'>
          You don&apos;t have any personal API tokens yet.
        </p>
      ) : (
        <div className='flex flex-col gap-2'>
          {tokens.map((token) => (
            <Card key={token.id}>
              <CardContent className='flex items-center justify-between gap-4'>
                <div className='flex flex-col gap-1 min-w-0'>
                  <div className='flex items-center gap-2'>
                    <h5 className='font-bold tracking-tight text-zinc-900 dark:text-zinc-50 truncate'>
                      {token.name}
                    </h5>
                    <Badge
                      variant={token.is_active ? 'secondary' : 'outline'}
                      className='text-xs'
                    >
                      {token.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <span className='text-xs text-muted-foreground'>
                    Created {formatDate(token.created_at)}
                  </span>
                  <span className='text-xs text-muted-foreground'>
                    Expires {formatDate(token.expires_at)}
                  </span>
                  <span className='text-xs text-muted-foreground'>
                    Last used{' '}
                    {token.last_used_at
                      ? formatDate(token.last_used_at)
                      : 'Never'}
                  </span>
                </div>
                <Button
                  variant='outline'
                  className='shrink-0'
                  onClick={() => setTokenToRevoke(token)}
                >
                  Revoke
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <RevealNewTokenModal
        token={revealedToken}
        open={!!revealedToken}
        onOpenChange={(open) => {
          if (!open) setRevealedToken(null);
        }}
      />

      <RevokeTokenModal
        tokenName={tokenToRevoke?.name ?? null}
        open={!!tokenToRevoke}
        onOpenChange={(open) => {
          if (!open) setTokenToRevoke(null);
        }}
        onConfirm={() => {
          if (tokenToRevoke) revokeToken(tokenToRevoke.id);
        }}
        isPending={isRevoking}
      />
    </div>
  );
};
